import { Head, Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, Lock, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { investmentPath, investmentsPath } from '@/lib/routes'

import {
  InvestmentFormFields,
  type InvestmentFormData,
  type Option,
  type TermsData,
} from './_Form'

type InvestmentEdit = {
  id: number
  fund_id: number
  company_id: number
  date: string
  amount_invested: number
  investment_type: InvestmentFormData['investment_type']
  stage_at_investment: string | null
  locked: boolean
  safe_terms: TermsData | null
  note_terms: TermsData | null
  equity_terms: TermsData | null
}

type Props = {
  investment: InvestmentEdit
  funds: Option[]
  companies: Option[]
}

function pickTerms(inv: InvestmentEdit): TermsData {
  if (inv.investment_type === 'safe') return inv.safe_terms ?? {}
  if (inv.investment_type === 'convertible_note') return inv.note_terms ?? {}
  return inv.equity_terms ?? {}
}

function InvestmentsEdit({ investment, funds, companies }: Props) {
  const form = useForm<InvestmentFormData>({
    fund_id: investment.fund_id,
    company_id: investment.company_id,
    date: investment.date,
    amount_invested: investment.amount_invested,
    investment_type: investment.investment_type,
    stage_at_investment: investment.stage_at_investment ?? '',
    terms: pickTerms(investment),
  })

  const locked = investment.locked

  return (
    <>
      <Head title="Edit investment" />

      <div className="mx-auto max-w-4xl px-8 py-10">
        <Link
          href={investmentsPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to investments
        </Link>

        <PageHeader title="Edit investment" />

        {locked && (
          <Card className="mb-6 border-amber-500/40 bg-amber-500/5">
            <CardContent className="flex items-start gap-3 py-4 text-sm">
              <Lock className="mt-0.5 h-4 w-4 text-amber-600" />
              <div>
                <p className="font-medium">Locked</p>
                <p className="text-muted-foreground">
                  This investment has proceeds or conversions downstream. Edits are disabled to
                  keep those rows consistent.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (locked) return
            form.transform((data) => ({ investment: data }))
            form.patch(investmentPath(investment.id))
          }}
        >
          <InvestmentFormFields
            data={form.data}
            errors={form.errors as Record<string, string>}
            setData={form.setData}
            funds={funds}
            companies={companies}
            disabled={locked}
          />

          {!locked && (
            <div className="mt-6 flex justify-end gap-2">
              <Link href={investmentsPath()}>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          )}
        </form>

        {!locked && (
          <Card className="mt-6 border-destructive/40">
            <CardHeader>
              <CardTitle className="text-destructive">Delete investment</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={() => {
                  if (window.confirm('Delete this investment?')) {
                    router.delete(investmentPath(investment.id))
                  }
                }}
              >
                <Trash2 />
                Delete
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

export default Object.assign(InvestmentsEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
